import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import useCountDown from 'react-countdown-hook';
import { invalidateCachedRunningVms } from 'src/features/room/room.slice';
interface BootProgressContextValue {
  bootProgress: number;
  bootTimeRemaining: number;
  startBootTimeCountdown: (milliseconds: number) => void;
  resetBootTimeCountdown: () => void;
}
const BootProgressContext = createContext<BootProgressContextValue | null>(null);
interface BootProgressProviderProps {
  children: ReactNode;
  bootTimeRemainingSeconds?: number;
  bootTime?: number;
  isVmReady?: boolean;
}
export const BootProgressProvider = ({
  children,
  bootTimeRemainingSeconds,
  bootTime,
  isVmReady
}: BootProgressProviderProps) => {
  const [bootTimeRemaining, {
    start: startBootTimeCountdown,
    reset: resetBootTimeCountdown
  }] = useCountDown(0, 1000);
  useEffect(() => {
    if (isVmReady) {
      resetBootTimeCountdown();
    } else if (bootTimeRemainingSeconds && bootTimeRemainingSeconds > 0) {
      startBootTimeCountdown(bootTimeRemainingSeconds * 1000);
    } else {
      resetBootTimeCountdown();
    }
  }, [bootTimeRemainingSeconds, isVmReady, startBootTimeCountdown, resetBootTimeCountdown]);
  useEffect(() => {
    if (bootTimeRemaining === 0 && bootTime !== 0) {
      setTimeout(() => invalidateCachedRunningVms(), 1000);
    }
  }, [bootTimeRemaining, bootTime]);
  const bootProgress = useMemo(() => {
    const safeBootTime = bootTime || 2;
    const bootTimeSeconds = 60 * safeBootTime;
    return Math.round((bootTimeSeconds - bootTimeRemaining / 1000) / bootTimeSeconds * 100);
  }, [bootTimeRemaining, bootTime]);
  const value = useMemo(() => ({
    bootProgress,
    bootTimeRemaining,
    startBootTimeCountdown,
    resetBootTimeCountdown
  }), [bootProgress, bootTimeRemaining, startBootTimeCountdown, resetBootTimeCountdown]);
  return <BootProgressContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="BootProgressProvider" data-sentry-source-file="index.tsx">{children}</BootProgressContext.Provider>;
};
export const useBootProgress = () => {
  const context = useContext(BootProgressContext);
  if (!context) {
    throw new Error('useBootProgress must be used within a BootProgressProvider');
  }
  return context;
};